import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TopNav from './TopNav';
import { getTrades, getAllTrades } from '../services/api';

interface Trade {
  id: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  filledQuantity: number;
  averagePrice: number;
  status: 'PENDING' | 'FILLED' | 'CANCELED' | 'REJECTED';
  createdAt: string;
  filledAt?: string;
  totalValue: number;
  commission: number;
  netAmount: number;
  realizedPnl?: number | null;
  // Admin-specific fields
  account?: {
    id: string;
    environment: string;
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
    };
  };
}

/**
 * Trade History Page
 * Displays comprehensive trade history with filtering and search
 */
const TradeHistory: React.FC = () => {
  const { user } = useAuth();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    symbol: '',
    side: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [filters.symbol, filters.side, filters.status, filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchTrades();
  }, [filters, pagination.currentPage, pagination.pageSize]);

  const fetchTrades = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Calculate offset from current page
      const offset = (pagination.currentPage - 1) * pagination.pageSize;

      // Use admin endpoint if user is admin, otherwise use regular endpoint
      const response = user?.role === 'ADMIN' 
        ? await getAllTrades({ ...filters, limit: pagination.pageSize, offset })
        : await getTrades({ ...filters, limit: pagination.pageSize, offset });
      
      // Handle response - API service now normalizes to response.data.trades
      if (response.success && response.data) {
        const trades = response.data.trades || [];
        const paginationInfo = response.data.pagination || {};
        
        setTrades(trades);
        setPagination(prev => ({
          ...prev,
          total: paginationInfo.total || trades.length,
          totalPages: paginationInfo.totalPages || Math.ceil((paginationInfo.total || trades.length) / prev.pageSize)
        }));
      } else {
        // Handle error response
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : (response.error as any)?.message || 'Failed to fetch trades';
        setError(errorMessage);
        setTrades([]);
      }
    } catch (error) {
      console.error('Failed to fetch trades:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch trades');
      setTrades([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      symbol: '',
      side: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    // Reset to first page when clearing filters
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination(prev => ({ 
      ...prev, 
      pageSize: newPageSize,
      currentPage: 1 // Reset to first page when changing page size
    }));
  };

  const formatCurrency = (amount: number): string => {
    // Handle NaN, null, undefined, and invalid numbers
    if (isNaN(amount) || amount === null || amount === undefined || !isFinite(amount)) {
      return '$0.00';
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    }) + ' ET';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'FILLED':
        return 'text-green-500 bg-green-100';
      case 'PENDING':
        return 'text-yellow-500 bg-yellow-100';
      case 'CANCELED':
        return 'text-gray-500 bg-gray-100';
      case 'REJECTED':
        return 'text-red-500 bg-red-100';
      default:
        return 'text-gray-500 bg-gray-100';
    }
  };

  const getSideColor = (side: string) => {
    return side === 'BUY' ? 'text-green-500' : 'text-red-500';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            <span className="ml-3 text-neutral-400">Loading trade history...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Trade History</h1>
            <p className="text-neutral-400">
              View and analyze your complete trading history
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Filters */}
          <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">Filters</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Symbol
                </label>
                <input
                  type="text"
                  name="symbol"
                  value={filters.symbol}
                  onChange={handleFilterChange}
                  className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="e.g., AAPL"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Side
                </label>
                <select
                  name="side"
                  value={filters.side}
                  onChange={handleFilterChange}
                  className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">All</option>
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">All</option>
                  <option value="FILLED">Filled</option>
                  <option value="PENDING">Pending</option>
                  <option value="CANCELED">Cancelled</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-2 rounded-lg font-medium transition-colors min-h-touch"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Trades Table */}
          <div className="bg-neutral-900 border border-neutral-700 rounded-lg overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <h2 className="text-base sm:text-lg font-semibold text-white">
                    Trade History
                  </h2>
                  <span className="text-sm text-neutral-400">
                    {pagination.total > 0 
                      ? `Showing ${((pagination.currentPage - 1) * pagination.pageSize) + 1}-${Math.min(pagination.currentPage * pagination.pageSize, pagination.total)} of ${pagination.total} trades`
                      : 'No trades'
                    }
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={pagination.pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                  <button
                    onClick={fetchTrades}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg font-medium transition-colors min-h-touch min-w-touch"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {trades.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-neutral-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-white mb-2">No trades found</h3>
                <p className="text-neutral-400">
                  {Object.values(filters).some(f => f) 
                    ? 'Try adjusting your filters to see more results.'
                    : 'Start trading to see your trade history here.'
                  }
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-800">
                    <tr>
                      {user?.role === 'ADMIN' && (
                        <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                          User
                        </th>
                      )}
                      <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Symbol
                      </th>
                      <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Side
                      </th>
                      <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider hidden sm:table-cell">
                        Quantity
                      </th>
                      <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider hidden md:table-cell">
                        Total Value
                      </th>
                      <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        P&L
                      </th>
                      <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider hidden lg:table-cell">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-700">
                    {trades.map((trade) => {
                      // Calculate P&L display
                      const getPnLDisplay = () => {
                        if (trade.side === 'BUY') {
                          return <span className="text-sm text-neutral-500">—</span>;
                        }
                        
                        // For SELL trades, show realized P&L
                        if (trade.realizedPnl !== null && trade.realizedPnl !== undefined) {
                          const pnl = Number(trade.realizedPnl);
                          const isProfit = pnl > 0;
                          const isLoss = pnl < 0;
                          
                          return (
                            <span className={`text-sm font-medium ${
                              isProfit ? 'text-green-400' : isLoss ? 'text-red-400' : 'text-neutral-300'
                            }`}>
                              {isProfit ? '+' : ''}{formatCurrency(pnl)}
                            </span>
                          );
                        }
                        
                        // If P&L not available, show dash
                        return <span className="text-sm text-neutral-500">—</span>;
                      };
                      
                      return (
                        <tr key={trade.id} className="hover:bg-neutral-800/50">
                          {user?.role === 'ADMIN' && (
                            <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-white">
                                <div className="font-medium">
                                  {trade.account?.user?.firstName} {trade.account?.user?.lastName}
                                </div>
                                <div className="text-xs text-neutral-400">
                                  {trade.account?.user?.email}
                                </div>
                                <div className="text-xs text-neutral-500">
                                  {trade.account?.environment}
                                </div>
                              </div>
                            </td>
                          )}
                          <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <span className="text-xs sm:text-sm font-medium text-white">
                              {trade.symbol}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <span className={`text-xs sm:text-sm font-medium ${getSideColor(trade.side)}`}>
                              {trade.side}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-neutral-300 hidden sm:table-cell">
                            {trade.filledQuantity.toLocaleString()}
                          </td>
                          <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-neutral-300">
                            {formatCurrency(trade.averagePrice)}
                          </td>
                          <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-neutral-300 hidden md:table-cell">
                            {formatCurrency(trade.totalValue)}
                          </td>
                          <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                            {getPnLDisplay()}
                          </td>
                          <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(trade.status)}`}>
                              {trade.status}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-neutral-300 hidden lg:table-cell">
                            {formatDate(trade.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {trades.length > 0 && pagination.totalPages > 1 && (
              <div className="px-4 sm:px-6 py-4 border-t border-neutral-700">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-neutral-400">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.currentPage === 1}
                      className="px-3 py-2 bg-neutral-800 border border-neutral-600 rounded-lg text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors min-h-touch min-w-touch"
                      aria-label="First page"
                    >
                      First
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="px-3 py-2 bg-neutral-800 border border-neutral-600 rounded-lg text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors min-h-touch min-w-touch"
                      aria-label="Previous page"
                    >
                      Previous
                    </button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-touch min-w-touch ${
                              pagination.currentPage === pageNum
                                ? 'bg-cyan-600 text-white'
                                : 'bg-neutral-800 border border-neutral-600 text-white hover:bg-neutral-700'
                            }`}
                            aria-label={`Page ${pageNum}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="px-3 py-2 bg-neutral-800 border border-neutral-600 rounded-lg text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors min-h-touch min-w-touch"
                      aria-label="Next page"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="px-3 py-2 bg-neutral-800 border border-neutral-600 rounded-lg text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors min-h-touch min-w-touch"
                      aria-label="Last page"
                    >
                      Last
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Summary Stats */}
          {trades.length > 0 && (
            <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-400">Total Trades</p>
                    <p className="text-2xl font-bold text-white">{pagination.total || trades.length}</p>
                  </div>
                  <div className="h-12 w-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-400">Total Volume</p>
                    <p className="text-2xl font-bold text-white">
                      {formatCurrency(trades.reduce((sum, trade) => sum + trade.totalValue, 0))}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-green-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-400">Filled Trades</p>
                    <p className="text-2xl font-bold text-white">
                      {trades.filter(t => t.status === 'FILLED').length}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-400">Avg Trade Size</p>
                    <p className="text-2xl font-bold text-white">
                      {formatCurrency(trades.reduce((sum, trade) => sum + trade.totalValue, 0) / trades.length)}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-purple-600/20 rounded-lg flex items-center justify-center">
                    <svg className="h-6 w-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradeHistory;
